import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  AvailableClass,
  PendingEnrollmentRow,
} from '../../types/PendingEnrollment.type';
import ColorChip from '../common/ColorChip';
import {
  levelAlternativeChipColors,
  levelChipColors,
} from '../../utils/colorHandler';
import {
  addPendingEnrollment,
  getAvailableClasses,
} from '../../utils/pendingEnrollmentApi';
import { notifyAPIClient, notifyFailure } from '../../utils/toast';

type Props = {
  open: boolean;
  row: PendingEnrollmentRow | null;
  onClose: () => void;
  onAdded: (registrationId: number) => void;
};

const AvailableClassesDialog = ({ open, row, onClose, onAdded }: Props) => {
  const [classes, setClasses] = useState<AvailableClass[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState<number | null>(null);
  const [confirmingClass, setConfirmingClass] = useState<AvailableClass | null>(
    null,
  );

  const levelPalette = {
    ...levelChipColors,
    ...levelAlternativeChipColors,
  };

  const handleClose = () => {
    setConfirmingClass(null);
    onClose();
  };

  useEffect(() => {
    if (!open || !row) return;
    let mounted = true;
    setLoading(true);
    getAvailableClasses(row.registrationId)
      .then((items) => {
        if (mounted) setClasses(items);
      })
      .catch(() => {
        if (mounted) notifyFailure();
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [open, row]);

  const handleConfirmAdd = async () => {
    if (!row || !confirmingClass) return;
    try {
      setSubmitting(confirmingClass.id);
      const response = await addPendingEnrollment(
        row.registrationId,
        confirmingClass.id,
      );
      notifyAPIClient(response.message);
      onAdded(row.registrationId);
      handleClose();
    } catch (error) {
      const response = (
        error as { response?: { status?: number; data?: { message?: string } } }
      ).response;
      if (response?.status === 409) {
        notifyAPIClient(
          response.data?.message || 'This class has just become full.',
        );
      } else {
        notifyFailure();
      }
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        Available classes{row ? ` — ${row.fullName}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : classes.length === 0 ? (
          <Typography color="text.secondary">
            No matching class with available capacity.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Class</TableCell>
                  <TableCell>Date / time</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Coach</TableCell>
                  <TableCell>Capacity</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {classes.map((classItem) => (
                  <TableRow key={classItem.id} hover>
                    <TableCell>{classItem.name}</TableCell>
                    <TableCell>
                      {classItem.classDate} - {classItem.startTime || '-'}
                      {classItem.endTime ? ` - ${classItem.endTime}` : ''}
                    </TableCell>
                    <TableCell>
                      <ColorChip
                        label={
                          classItem.level?.trim().toLowerCase() === 'none'
                            ? null
                            : classItem.level
                        }
                        palette={levelPalette}
                        emptyLabel="None"
                      />
                    </TableCell>
                    <TableCell>
                      {classItem.coachNames.join(', ') || '—'}
                    </TableCell>
                    <TableCell>
                      {classItem.studentCount}/{classItem.maxStudents} (
                      {classItem.availableSeats} free)
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={submitting !== null}
                        onClick={() => setConfirmingClass(classItem)}
                      >
                        {submitting === classItem.id ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          'Add'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
      <Dialog
        open={Boolean(confirmingClass)}
        onClose={() => setConfirmingClass(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm add student</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Add <strong>{row?.fullName}</strong> to{' '}
            <strong>{confirmingClass?.name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmingClass(null)}
            disabled={submitting !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleConfirmAdd()}
            variant="contained"
            disabled={submitting !== null}
          >
            {submitting !== null ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AvailableClassesDialog;
